import { useMemo } from 'react';
import { useAppData } from '../../../providers/AppDataProvider';
import { useInvoice } from '../../invoices/contexts/InvoiceContext';
import { useBills } from '../../bills/contexts/BillsContext';
import { useEmailPanel } from '../../mail/contexts/EmailPanelContext';
import { normalizeEmailFolder } from '../../mail/utils/emailFolder';
import type { EmailDraft } from '../../../services/draftService';

/**
 * An item surfaced in the Attention Queue. Items are sorted into four
 * categories (URGENT / TODAY / WAITING / ACTIVE) and priority-scored within
 * each category so the most urgent row floats to the top.
 */
export type AttentionCategory = 'urgent' | 'today' | 'waiting_on_you' | 'waiting_on_them';

export type AttentionItemKind =
  | 'overdue_invoice'
  | 'overdue_bill'
  | 'overdue_job'
  | 'due_invoice'
  | 'due_bill'
  | 'due_job'
  | 'unread_email'
  | 'stale_draft'
  | 'awaiting_client_job';

export interface AttentionItem {
  id: string;              // stable unique key, prefixed by kind
  kind: AttentionItemKind;
  category: AttentionCategory;
  statusCode: 'OVERDUE' | 'DUE' | 'UNREAD' | 'DRAFT' | 'WAIT';
  title: string;           // primary (customer / subject / job label)
  subtitle: string;        // secondary (amount / age / reference)
  actionLabel: string;     // e.g. "open", "reply", "resume"
  priority: number;        // higher = more urgent within category
  payload: any;            // the underlying record so the row click can open it
}

const DAY_MS = 24 * 60 * 60 * 1000;

function daysBetween(laterDate: Date, earlierDate: Date): number {
  return Math.floor((laterDate.getTime() - earlierDate.getTime()) / DAY_MS);
}

function hoursSince(dateValue: Date): number {
  return Math.floor((Date.now() - dateValue.getTime()) / (60 * 60 * 1000));
}

function compactCurrency(n: number): string {
  if (!isFinite(n) || n === 0) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

function isUnpaidStatus(status: string | undefined): boolean {
  const s = String(status || '').toUpperCase();
  return s !== 'PAID' && s !== 'VOIDED' && s !== 'DELETED';
}

function safeDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  // Firestore Timestamp
  if (typeof input.toDate === 'function') {
    try {
      const d = input.toDate();
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (typeof input === 'string' || typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * useAttentionItems — derives a ranked, categorized list of actionable
 * items from existing context hooks. Pure computation; no new queries.
 */
export function useAttentionItems(): {
  urgent: AttentionItem[];
  today: AttentionItem[];
  waitingOnYou: AttentionItem[];
  waitingOnThem: AttentionItem[];
  total: number;
} {
  const { jobs, customers } = useAppData();
  // Native accounting contexts — these provide the canonical Invoice/Bill
  // shapes used by InvoiceViewModal / BillViewModal, so the payload we
  // stash here can flow straight into those modals with no translation.
  const { invoices } = useInvoice();
  const { bills } = useBills();
  const { preLoadedEmails, userDrafts, liveInboxIds } = useEmailPanel();

  return useMemo(() => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    const urgent: AttentionItem[] = [];
    const todayItems: AttentionItem[] = [];
    const waitingOnYou: AttentionItem[] = [];
    const waitingOnThem: AttentionItem[] = [];

    const customerNameById = new Map<string, string>();
    for (const c of customers) {
      if (c.id) customerNameById.set(c.id, c.name || c.company || c.email || 'unknown');
    }
    const resolveCustomerName = (ids: string[] | string | undefined): string => {
      if (!ids) return '';
      if (typeof ids === 'string') return customerNameById.get(ids) || '';
      return ids.map(id => customerNameById.get(id) || '').filter(Boolean).join(', ');
    };

    // ── Overdue / due invoices (native Invoice shape) ────────────────────
    for (const inv of invoices) {
      if (!isUnpaidStatus(inv.status)) continue;
      const dueDate = safeDate(inv.dueDate);
      if (!dueDate) continue;
      const daysLate = daysBetween(today, dueDate);
      const amount = Number(inv.amountDue) || 0;
      const contact = inv.contactName || 'unknown';

      if (daysLate > 0) {
        urgent.push({
          id: `overdue_inv_${inv.id}`,
          kind: 'overdue_invoice',
          category: 'urgent',
          statusCode: 'OVERDUE',
          title: contact,
          subtitle: `inv ${inv.invoiceNumber || inv.id.slice(-6)} · ${compactCurrency(amount)} · ${daysLate}d late`,
          actionLabel: 'open',
          priority: 1000 + daysLate * 100 + amount / 1000,
          payload: { kind: 'invoice', id: inv.id, invoice: inv },
        });
      } else if (daysLate === 0) {
        todayItems.push({
          id: `due_inv_${inv.id}`,
          kind: 'due_invoice',
          category: 'today',
          statusCode: 'DUE',
          title: contact,
          subtitle: `inv ${inv.invoiceNumber || inv.id.slice(-6)} · ${compactCurrency(amount)} · due today`,
          actionLabel: 'open',
          priority: 500 + amount / 1000,
          payload: { kind: 'invoice', id: inv.id, invoice: inv },
        });
      }
    }

    // ── Overdue / due bills (native Bill shape) ──────────────────────────
    for (const bill of bills) {
      if (!isUnpaidStatus(bill.status)) continue;
      const dueDate = safeDate(bill.dueDate);
      if (!dueDate) continue;
      const daysLate = daysBetween(today, dueDate);
      const amount = Number(bill.amountDue) || 0;
      const contact = bill.contactName || 'unknown';

      if (daysLate > 0) {
        urgent.push({
          id: `overdue_bill_${bill.id}`,
          kind: 'overdue_bill',
          category: 'urgent',
          statusCode: 'OVERDUE',
          title: contact,
          subtitle: `bill ${bill.billNumber || bill.id.slice(-6)} · ${compactCurrency(amount)} · ${daysLate}d late (we owe)`,
          actionLabel: 'open',
          priority: 900 + daysLate * 90 + amount / 1000,
          payload: { kind: 'bill', id: bill.id, bill },
        });
      } else if (daysLate === 0) {
        todayItems.push({
          id: `due_bill_${bill.id}`,
          kind: 'due_bill',
          category: 'today',
          statusCode: 'DUE',
          title: contact,
          subtitle: `bill ${bill.billNumber || bill.id.slice(-6)} · ${compactCurrency(amount)} · due today`,
          actionLabel: 'open',
          priority: 400 + amount / 1000,
          payload: { kind: 'bill', id: bill.id, bill },
        });
      }
    }

    // ── Jobs: overdue / due today / awaiting client ──────────────────────
    // Only surface jobs in an explicitly-active state. Any status outside
    // this set — including canonical terminal states (Closed/Cancelled/
    // Archived) AND legacy/typo values like 'Complete' — is excluded from
    // the attention feeds.
    const ACTIVE_JOB_STATUSES = new Set(['Pending', 'Open', 'Active', 'Awaiting Client Response', 'Overdue']);
    for (const job of jobs) {
      if (!ACTIVE_JOB_STATUSES.has(String(job.status))) continue;
      const customer = resolveCustomerName(job.customerId) || 'unknown';
      const dueDate = safeDate(job.dueDate);
      const daysLate = dueDate ? daysBetween(today, dueDate) : null;

      // Overdue jobs
      if ((job.status === 'Overdue') || (daysLate !== null && daysLate > 0 && (job.status === 'Open' || job.status === 'Pending' || job.status === 'Active'))) {
        const late = Math.max(daysLate || 0, 1);
        urgent.push({
          id: `overdue_job_${job.id}`,
          kind: 'overdue_job',
          category: 'urgent',
          statusCode: 'OVERDUE',
          title: customer,
          subtitle: `job ${job.jobLabel || '(untitled)'} · ${late}d past due`,
          actionLabel: 'open',
          priority: 800 + late * 80,
          payload: { kind: 'job', id: job.id, job },
        });
        continue;
      }

      // Due today
      if (daysLate === 0) {
        todayItems.push({
          id: `due_job_${job.id}`,
          kind: 'due_job',
          category: 'today',
          statusCode: 'DUE',
          title: customer,
          subtitle: `job ${job.jobLabel || '(untitled)'} · due today`,
          actionLabel: 'open',
          priority: 300,
          payload: { kind: 'job', id: job.id, job },
        });
        continue;
      }

      // Awaiting client response
      if (job.status === 'Awaiting Client Response') {
        const lastEditedDate = safeDate(job.lastEdited) || safeDate(job.creationDate);
        const daysSince = lastEditedDate ? daysBetween(now, lastEditedDate) : 0;
        waitingOnThem.push({
          id: `awaiting_${job.id}`,
          kind: 'awaiting_client_job',
          category: 'waiting_on_them',
          statusCode: 'WAIT',
          title: customer,
          subtitle: `job ${job.jobLabel || '(untitled)'} · ${daysSince}d since last activity`,
          actionLabel: 'open',
          priority: 40 + daysSince,
          payload: { kind: 'job', id: job.id, job },
        });
      }
    }

    // ── Unread customer emails ────────────────────────────────────────────
    // Gate on the live-inbox allowlist. preLoadedEmails is a shared bucket
    // that also holds transient search / historical hydration; liveInboxIds
    // is exactly the set covered by the canonical 30-day subscription, so
    // gating on membership guarantees "Waiting on you" reflects only the
    // live inbox. A year-old newsletter pulled in by a mailbox search isn't
    // in the snapshot, so it can never leak here — no per-caller tagging or
    // declassify bookkeeping required. (Before the first snapshot lands the
    // set is empty; nothing surfaces, which is correct — we don't yet know
    // the live inbox.)
    const unreadEmails = (preLoadedEmails || []).filter(e => {
      if (!liveInboxIds.has(e.id)) return false;
      if (e.isRead) return false;
      if (e.dismissed) return false;
      if (e.deleted) return false;
      // Only genuine inbox mail: skip sent/drafts/junk/deleted AND mail the owner
      // filed into an Outlook folder (archive/filed). normalizeEmailFolder also
      // maps the old deleted:true + folder:'inbox' "moved out" mislabel to
      // 'deleted', so such a doc can never surface as "waiting for you".
      if (normalizeEmailFolder(e) !== 'inbox') return false;
      return true;
    });
    // No cap on the unread-email window: every unread inbox email in the live
    // 30-day snapshot surfaces. The count is already bounded by that live set,
    // so there's nothing to slice — a fixed cap here silently dropped real
    // client mail (the owner's #1 priority) once the unread pile grew, which is
    // exactly what we don't want. The wall groups these by conversation and the
    // home queue lists them. Most recent first (unreadEmails is date-desc).
    for (const email of unreadEmails) {
      const fromName = email.from?.[0]?.name || email.from?.[0]?.address || email.sender || 'unknown';
      const emailDate = safeDate(email.createdAt) || safeDate(email.date);
      const hours = emailDate ? hoursSince(emailDate) : 0;
      const ageLabel = hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
      waitingOnYou.push({
        id: `unread_${email.id}`,
        kind: 'unread_email',
        category: 'waiting_on_you',
        statusCode: 'UNREAD',
        title: fromName,
        subtitle: `${email.subject || '(no subject)'} · ${ageLabel}`,
        actionLabel: 'reply',
        priority: 50 + Math.min(hours, 72),
        payload: { kind: 'email', id: email.id, email },
      });
    }

    // ── Stale drafts (older than 1h, still in progress) ──────────────────
    const staleDrafts: EmailDraft[] = (userDrafts || []).filter(d => {
      if ((d.status ?? 'draft') !== 'draft') return false;
      const updated = safeDate(d.updatedAt);
      if (!updated) return false;
      return hoursSince(updated) >= 1;
    });
    for (const draft of staleDrafts) {
      const updated = safeDate(draft.updatedAt);
      const hours = updated ? hoursSince(updated) : 0;
      const ageLabel = hours < 24 ? `${hours}h in progress` : `${Math.floor(hours / 24)}d in progress`;
      const recipient = (draft.to || [])[0] || '(no recipient)';
      waitingOnYou.push({
        id: `draft_${draft.id}`,
        kind: 'stale_draft',
        category: 'waiting_on_you',
        statusCode: 'DRAFT',
        title: draft.subject || '(no subject)',
        subtitle: `to ${recipient} · ${ageLabel}`,
        actionLabel: 'resume',
        priority: 30 + Math.min(hours, 48),
        payload: { kind: 'draft', id: draft.id, draft },
      });
    }

    // Sort each category by priority desc
    const byPriorityDesc = (a: AttentionItem, b: AttentionItem) => b.priority - a.priority;
    urgent.sort(byPriorityDesc);
    todayItems.sort(byPriorityDesc);
    waitingOnYou.sort(byPriorityDesc);
    waitingOnThem.sort(byPriorityDesc);

    return {
      urgent,
      today: todayItems,
      waitingOnYou,
      waitingOnThem,
      total: urgent.length + todayItems.length + waitingOnYou.length + waitingOnThem.length,
    };
  }, [invoices, bills, jobs, customers, preLoadedEmails, userDrafts, liveInboxIds]);
}
